/**
 * Westwood's job titles, as used by this application.
 *
 * A title is typed on the User Management page - any title, since migration
 * 020. Seven are offered as suggestions, and they are the only ones that may
 * carry edit rights:
 *
 *   Admin, Construction Manager  ->  Admin, Project Manager, User or View only
 *   Project Manager              ->  Project Manager, User or View only
 *   VP of Property Management    ->  VP of Property Management, User or View
 *                                    only (migration 026)
 *   Senior Property Manager,
 *   Property Manager, APM        ->  User or View only
 *   any other title, or none     ->  View only
 *
 * The title is a ceiling, not a grant: what a person may see and change still
 * comes from app_user.role and from being named on a project. The admin picks
 * the access level; this only narrows the choices offered.
 *
 * The API holds the same table in api/src/lib/userAccess.ts and enforces it -
 * this copy decides what the screen offers, that one decides what is saved.
 * api/test/user-access.test.ts reads ROLES_BY_TITLE below and fails if the two
 * disagree, so keep it a plain literal: one title per line, quoted.
 */
export const ROLES_BY_TITLE = {
  'Admin': ['admin', 'project_manager', 'user', 'viewer'],
  'Construction Manager': ['admin', 'project_manager', 'user', 'viewer'],
  'Project Manager': ['project_manager', 'user', 'viewer'],
  'VP of Property Management': ['vp_property', 'user', 'viewer'],
  'Senior Property Manager': ['user', 'viewer'],
  'Property Manager': ['user', 'viewer'],
  'APM': ['user', 'viewer'],
};

/** Every other title, and no title at all. */
export const OTHER_TITLE_ROLES = ['viewer'];

/** The titles offered as suggestions on the User Management page. */
export const JOB_TITLES = Object.keys(ROLES_BY_TITLE);

/**
 * A typed title as the API will save it: trimmed, inner spaces collapsed,
 * blank as null, and one of the five in its own spelling whatever the case
 * it was typed in. Mirrors normaliseJobTitle in api/src/lib/userAccess.ts.
 */
export function normaliseJobTitle(value) {
  const tidy = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!tidy) return null;
  return JOB_TITLES.find(t => t.toLowerCase() === tidy.toLowerCase()) ?? tidy;
}

/** The access levels a title allows, widest first. */
export function rolesForTitle(title) {
  const canonical = normaliseJobTitle(title);
  return (canonical && ROLES_BY_TITLE[canonical]) || OTHER_TITLE_ROLES;
}

/**
 * Which titles belong in the Property Manager, Senior Property Manager and
 * APM pickers.
 *
 * Written once, here, because the two pickers sit side by side on the project
 * form and the failure mode of keeping the rule in both places is that they
 * drift and someone quietly disappears from one of them.
 *
 * Listed rather than derived. Before 020 the Property Manager list was
 * "every title but APM", which was right while there were three titles; with
 * Admin, Construction Manager and Project Manager among them it would have put
 * those people first in the Property Manager box. VP of Property Management -
 * a suggested title again since migration 026 - is offered first there too.
 *
 * A Senior Property Manager is offered first in both their own box and the
 * Property Manager box: the same person often holds both posts on a project.
 * The APM box stays apart - nobody is offered first there and elsewhere.
 */
export const APM_TITLES = ['APM'];

export const SENIOR_PROPERTY_MANAGER_TITLES = ['Senior Property Manager'];

export const PROPERTY_MANAGER_TITLES = [
  'Property Manager',
  'Senior Property Manager',
  'VP of Property Management',
];

/**
 * Split a directory into the people whose title is in `titles` and everyone
 * else, each half sorted by the name actually shown.
 *
 * Everyone else is kept rather than dropped. Westwood is small enough that the
 * exception happens - a property manager covering as APM on one project, the
 * VP named as the property manager on their own building - and a picker that
 * refuses to offer the right person is worse than one that offers too many.
 * The grouping puts the expected people first; it does not rule the rest out.
 */
export function groupByTitle(users, titles) {
  const preferred = new Set(titles);
  const byName = (a, b) => displayName(a).localeCompare(displayName(b));
  const list = users || [];
  return {
    matching: list.filter(u => preferred.has(u.job_title)).sort(byName),
    others: list.filter(u => !preferred.has(u.job_title)).sort(byName),
  };
}

/** The name to show for someone, falling back to their email. */
export function displayName(user) {
  return user?.full_name || user?.email || '';
}

/** "Barbara Poggi — Property Manager", or just the name if we have no title. */
export function displayNameWithTitle(user) {
  const name = displayName(user);
  return user?.job_title ? `${name} — ${user.job_title}` : name;
}
